import { useInterval } from '@btcdirect/shared/src/composables/util/interval';
import { getSecondsUntilDateTime } from '@btcdirect/shared/src/composables/util/time';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useTimersStore } from './timers';

interface AdyenPaymentSessionContext {
    sessionData: string;
}

export interface BankTransferPaymentSessionContext {
    name: string; // BTC Direct Europe BV
    bank: string; // BUNQ
    iban: string; // NL16BUNQ2096044065
    swift: string; // BUNQNL2A
    city: string; // Nijmegen
    country: string; // The Netherlands
    reference: string; // 07971781
}

export interface PaymentSession {
    id: string;
    transactionId: string;
    returnUrl: string;
    expiresAt: string;
    orderId: number;
    context?: AdyenPaymentSessionContext | BankTransferPaymentSessionContext;
    sessionData?: string;
    initialSessionDuration?: number;
}

export const defaultPaymentSession: PaymentSession = Object.freeze({
    id: null,
    transactionId: null,
    returnUrl: null,
    expiresAt: null,
    orderId: null,
    context: null,
    sessionData: null,
    initialSessionDuration: null
});

export const usePaymentSessionStore = defineStore('payment-session', () => {
    const session = ref<PaymentSession>(defaultPaymentSession);

    const timersStore = useTimersStore();
    const { startSingleCountdown, stop } = useInterval();

    const TIMER = 'payment-session-timer';

    function setPaymentSession(paymentSession: PaymentSession): void {
        if (!paymentSession) {
            reset();
            return;
        }

        session.value = {
            ...defaultPaymentSession,
            ...paymentSession,
            initialSessionDuration: Math.round(getSecondsUntilDateTime(paymentSession.expiresAt) / 60)
        };
    }

    function reset(): void {
        // todo: why does this need a timeout? bad for unit tests, look into it
        setTimeout(() => {
            session.value = {
                ...defaultPaymentSession,
                initialSessionDuration: session.value.initialSessionDuration
            };

            timersStore.countdowns.set(TIMER, getSecondsUntilDateTime(session.value.expiresAt));
        }, 500);
    }

    return {
        startPaymentSessionTimer: (callback?: () => Promise<void>) =>
            startSingleCountdown(TIMER, getSecondsUntilDateTime(session.value.expiresAt) * 1000, 10, callback),
        stopPaymentSessionTimer: () => stop(TIMER),
        countdown: computed<number>(() => timersStore.countdowns.get(TIMER)),
        paymentSession: computed<PaymentSession>(() => session.value),
        setPaymentSession,
        reset
    };
});

export function getSessionData(session: PaymentSession): string {
    if (!!session.context && 'sessionData' in session.context && !!session.context.sessionData) {
        return session.context.sessionData;
    }

    return session.sessionData || null;
}
